/* ============================================
   LUCIDA COSMETICS — THEME.JS
   Vanilla JS, no dependencies
   ============================================ */

'use strict';

// ============================================
// UTILITIES
// ============================================

const Utils = {
  formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || window.pyramidTheme.moneyFormat;

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  },

  debounce(fn, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  showToast(message, type = 'default', duration = 3500) {
    let toast = document.getElementById('pyramid-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'pyramid-toast';
      toast.className = 'toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.className = `toast toast--${type}`;
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });
    setTimeout(() => {
      toast.classList.remove('is-visible');
    }, duration);
  },

  fetchJSON(url, options = {}) {
    return fetch(url, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...options.headers },
      ...options
    }).then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    });
  }
};

// ============================================
// ANNOUNCEMENT BAR
// ============================================

class AnnouncementBar {
  constructor() {
    this.bar = document.querySelector('.announcement-bar');
    this.closeBtn = document.querySelector('.announcement-bar__close');
    if (!this.bar || !this.closeBtn) return;
    this.init();
  }

  init() {
    if (sessionStorage.getItem('pyramid-announcement-closed')) {
      this.bar.classList.add('is-hidden');
      return;
    }
    this.closeBtn.addEventListener('click', () => this.close());
  }

  close() {
    this.bar.style.maxHeight = this.bar.offsetHeight + 'px';
    requestAnimationFrame(() => {
      this.bar.style.transition = 'max-height 0.35s ease, opacity 0.3s ease';
      this.bar.style.maxHeight = '0';
      this.bar.style.opacity = '0';
    });
    setTimeout(() => {
      this.bar.classList.add('is-hidden');
    }, 360);
    sessionStorage.setItem('pyramid-announcement-closed', '1');
  }
}

// ============================================
// STICKY HEADER
// ============================================

class StickyHeader {
  constructor() {
    this.header = document.querySelector('.site-header');
    if (!this.header) return;
    this.scrollY = 0;
    this.init();
  }

  init() {
    this.onScroll = Utils.debounce(this.handleScroll.bind(this), 10);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.handleScroll();
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    if (currentScrollY > 60) {
      this.header.classList.add('is-scrolled');
    } else {
      this.header.classList.remove('is-scrolled');
    }
    this.scrollY = currentScrollY;
  }
}

// ============================================
// MOBILE NAVIGATION
// ============================================

class MobileNav {
  constructor() {
    this.nav = document.querySelector('.mobile-nav');
    this.toggle = document.querySelector('.header__menu-toggle');
    this.closeBtn = document.querySelector('.mobile-nav__close');
    this.overlay = document.getElementById('overlay');
    if (!this.nav || !this.toggle) return;
    this.init();
  }

  init() {
    this.toggle.addEventListener('click', () => this.open());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    // Submenu toggles
    document.querySelectorAll('.mobile-nav__link[data-has-submenu]').forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault();
        const item = link.closest('.mobile-nav__item');
        const submenu = item.querySelector('.mobile-nav__submenu');
        const isOpen = link.classList.contains('is-open');

        // Close all
        document.querySelectorAll('.mobile-nav__link.is-open').forEach(l => l.classList.remove('is-open'));
        document.querySelectorAll('.mobile-nav__submenu.is-open').forEach(s => s.classList.remove('is-open'));

        if (!isOpen) {
          link.classList.add('is-open');
          if (submenu) submenu.classList.add('is-open');
        }
      });
    });

    // Close on Escape
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
  }

  open() {
    this.nav.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-active');
    document.body.classList.add('menu-open');
    this.toggle.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.nav.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-active');
    document.body.classList.remove('menu-open');
    this.toggle.setAttribute('aria-expanded', 'false');
  }
}

// ============================================
// SEARCH BAR
// ============================================

class SearchBar {
  constructor() {
    this.bar = document.querySelector('.search-bar');
    this.toggleBtns = document.querySelectorAll('[data-search-toggle]');
    this.closeBtns = document.querySelectorAll('[data-search-close]');
    this.input = document.querySelector('.search-bar__input');
    if (!this.bar) return;
    this.init();
  }

  init() {
    this.toggleBtns.forEach(btn => btn.addEventListener('click', () => this.toggle()));
    this.closeBtns.forEach(btn => btn.addEventListener('click', () => this.close()));
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
  }

  toggle() {
    this.bar.classList.contains('is-open') ? this.close() : this.open();
  }

  open() {
    this.bar.classList.add('is-open');
    setTimeout(() => this.input && this.input.focus(), 100);
  }

  close() {
    this.bar.classList.remove('is-open');
  }
}

// ============================================
// CART DRAWER
// ============================================

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('overlay');
    this.cartCountEls = document.querySelectorAll('[data-cart-count]');
    this.cartTotalEls = document.querySelectorAll('[data-cart-total]');
    this.shippingThreshold = (window.pyramidTheme && window.pyramidTheme.strings.freeShippingThreshold) || 0;
    if (!this.drawer) return;
    this.init();
  }

  init() {
    // Open triggers
    document.querySelectorAll('[data-cart-open]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        this.open();
      });
    });

    // Close
    const closeBtn = this.drawer.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });

    // Quantity steppers (delegated)
    this.drawer.addEventListener('click', e => {
      const btn = e.target.closest('[data-qty-btn]');
      if (btn) {
        e.preventDefault();
        this.handleQtyChange(btn);
      }
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        this.removeItem(removeBtn.dataset.line);
      }
    });

    this.drawer.addEventListener('change', e => {
      const input = e.target.closest('[data-qty-input]');
      if (input) {
        this.updateQuantity(input.dataset.line, parseInt(input.value));
      }
    });

    // Listen for cart:open events
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:refresh', () => this.refresh());
  }

  open() {
    this.drawer.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-active');
    document.body.classList.add('drawer-open');
    this.refresh();
  }

  close() {
    this.drawer.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-active');
    document.body.classList.remove('drawer-open');
  }

  async refresh() {
    try {
      const cart = await Utils.fetchJSON('/cart.js');
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart refresh failed:', err);
    }
  }

  renderCart(cart) {
    this.updateCount(cart.item_count);
    this.updateTotal(cart.total_price);
    this.updateShippingBar(cart.total_price);

    const body = this.drawer.querySelector('.cart-drawer__body');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>
          <p>Your cart is empty</p>
          <a href="/collections/all" class="btn btn--secondary btn--sm" onclick="window.cartDrawer && window.cartDrawer.close()">Continue Shopping</a>
        </div>`;
      return;
    }

    body.innerHTML = cart.items.map((item, index) => `
      <div class="cart-item" data-key="${item.key}">
        <a href="${item.url}">
          <img class="cart-item__image" src="${item.featured_image ? item.featured_image.url : ''}" alt="${item.product_title}" loading="lazy" width="90" height="110">
        </a>
        <div class="cart-item__details">
          ${item.vendor ? `<span class="cart-item__brand">${item.vendor}</span>` : ''}
          <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<span class="cart-item__variant">${item.variant_title}</span>` : ''}
          <div class="cart-item__footer">
            <div class="quantity-stepper">
              <button class="quantity-stepper__btn" data-qty-btn="minus" data-line="${index + 1}" aria-label="Decrease quantity">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="12" height="12"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/></svg>
              </button>
              <input class="quantity-stepper__input" type="number" value="${item.quantity}" min="1" data-qty-input data-line="${index + 1}" aria-label="Quantity">
              <button class="quantity-stepper__btn" data-qty-btn="plus" data-line="${index + 1}" aria-label="Increase quantity">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="12" height="12"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
              </button>
            </div>
            <span class="cart-item__price">${Utils.formatMoney(item.final_line_price)}</span>
          </div>
          <button class="cart-item__remove" data-remove-item data-line="${index + 1}">Remove</button>
        </div>
      </div>`).join('');
  }

  updateCount(count) {
    this.cartCountEls.forEach(el => {
      el.textContent = count;
      el.dataset.count = count;
    });
  }

  updateTotal(totalPrice) {
    this.cartTotalEls.forEach(el => {
      el.textContent = Utils.formatMoney(totalPrice);
    });
  }

  updateShippingBar(totalPrice) {
    const bar = this.drawer.querySelector('.shipping-progress__fill');
    const text = this.drawer.querySelector('.shipping-progress__text');
    if (!bar || !text) return;

    const thresholdCents = this.shippingThreshold * 100;
    const progress = Math.min((totalPrice / thresholdCents) * 100, 100);
    bar.style.width = progress + '%';

    if (totalPrice >= thresholdCents) {
      text.innerHTML = `<span>You've unlocked free shipping!</span> 🎉`;
    } else {
      const remaining = Utils.formatMoney(thresholdCents - totalPrice);
      text.innerHTML = `Add <span>${remaining}</span> more for free shipping`;
    }
  }

  handleQtyChange(btn) {
    const line = btn.dataset.line;
    const input = this.drawer.querySelector(`[data-qty-input][data-line="${line}"]`);
    if (!input) return;
    let qty = parseInt(input.value);
    if (btn.dataset.qtyBtn === 'plus') qty++;
    else qty = Math.max(0, qty - 1);
    input.value = qty;
    this.updateQuantity(line, qty);
  }

  async updateQuantity(line, quantity) {
    try {
      const cart = await Utils.fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ line, quantity })
      });
      this.renderCart(cart);
    } catch (err) {
      console.error('Update quantity failed:', err);
    }
  }

  async removeItem(line) {
    await this.updateQuantity(line, 0);
  }
}

// ============================================
// ADD TO CART
// ============================================

class AddToCart {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('submit', e => {
      const form = e.target.closest('[data-add-to-cart-form]');
      if (!form) return;
      e.preventDefault();
      this.handleSubmit(form);
    });

    // Quick add buttons on product cards
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (variantId) this.addItem(variantId, 1, btn);
    });
  }

  async handleSubmit(form) {
    const submitBtn = form.querySelector('[data-add-to-cart-btn]');
    const variantInput = form.querySelector('[name="id"]');
    const qtyInput = form.querySelector('[name="quantity"]');

    if (!variantInput) return;

    const variantId = variantInput.value;
    const quantity = qtyInput ? parseInt(qtyInput.value) : 1;

    this.setLoading(submitBtn, true);
    await this.addItem(variantId, quantity, submitBtn);
    this.setLoading(submitBtn, false);
  }

  async addItem(variantId, quantity, triggerEl) {
    if (triggerEl) this.setLoading(triggerEl, true);
    try {
      await Utils.fetchJSON('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ id: variantId, quantity })
      });
      Utils.showToast('Added to cart!', 'success');
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (err) {
      Utils.showToast('Could not add to cart. Please try again.', 'error');
      console.error('Add to cart failed:', err);
    } finally {
      if (triggerEl) this.setLoading(triggerEl, false);
    }
  }

  setLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      btn.dataset.originalText = btn.textContent;
      btn.innerHTML = '<span class="loading-spinner"></span>';
      btn.disabled = true;
    } else {
      btn.textContent = btn.dataset.originalText || 'Add to Cart';
      btn.disabled = false;
    }
  }
}

// ============================================
// PRODUCT GALLERY
// ============================================

class ProductGallery {
  constructor(el) {
    this.el = el;
    this.mainImg = el.querySelector('.product-gallery__main-img');
    this.thumbs = el.querySelectorAll('.product-gallery__thumb');
    if (!this.mainImg) return;
    this.init();
  }

  init() {
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.setActive(i));
    });
    // Click main image to open zoom
    this.mainImg.addEventListener('click', () => this.openZoom());
  }

  setActive(index) {
    const thumb = this.thumbs[index];
    if (!thumb) return;

    const fullSrc = thumb.dataset.fullSrc;
    const fullSrcset = thumb.dataset.fullSrcset;
    const zoomSrc = thumb.dataset.zoomSrc;
    if (!fullSrc) return;

    // Preload the full-resolution image, then swap (prevents a low-res flash)
    const preload = new Image();
    if (fullSrcset) preload.srcset = fullSrcset;
    preload.src = fullSrc;

    const swap = () => {
      // Order matters: clear srcset first so the new src is always honoured
      this.mainImg.removeAttribute('srcset');
      this.mainImg.src = fullSrc;
      if (fullSrcset) this.mainImg.srcset = fullSrcset;
      if (zoomSrc) this.mainImg.dataset.zoomSrc = zoomSrc;
      if (thumb.dataset.alt) this.mainImg.alt = thumb.dataset.alt;
      this.mainImg.style.opacity = '1';
    };

    this.mainImg.style.transition = 'opacity 0.2s ease';
    this.mainImg.style.opacity = '0';
    if (preload.complete) {
      setTimeout(swap, 120);
    } else {
      preload.onload = () => setTimeout(swap, 60);
      preload.onerror = swap;
      setTimeout(swap, 600); // safety net
    }

    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }

  openZoom() {
    const src = this.mainImg.dataset.zoomSrc || this.mainImg.currentSrc || this.mainImg.src;
    if (!src) return;

    let overlay = document.getElementById('product-zoom');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'product-zoom';
      overlay.className = 'product-zoom';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.innerHTML =
        '<button class="product-zoom__close" aria-label="Close">&times;</button>' +
        '<img class="product-zoom__img" alt="">';
      document.body.appendChild(overlay);

      const close = () => this.closeZoom();
      overlay.addEventListener('click', e => {
        if (e.target === overlay || e.target.classList.contains('product-zoom__close')) close();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') close();
      });
      // Click the zoomed image to toggle 2x magnification
      const zImg = overlay.querySelector('.product-zoom__img');
      zImg.addEventListener('click', e => {
        e.stopPropagation();
        zImg.classList.toggle('is-magnified');
      });
    }

    const zoomImg = overlay.querySelector('.product-zoom__img');
    zoomImg.classList.remove('is-magnified');
    zoomImg.src = src;
    zoomImg.alt = this.mainImg.alt || '';
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeZoom() {
    const overlay = document.getElementById('product-zoom');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

// ============================================
// VARIANT SELECTOR
// ============================================

class VariantSelector {
  constructor(el) {
    this.el = el;
    this.form = el.closest('form[data-add-to-cart-form]') || el.closest('.product-info');
    this.productData = null;
    this.selectedOptions = [];

    const dataEl = document.getElementById('product-json');
    if (dataEl) {
      try {
        this.productData = JSON.parse(dataEl.textContent);
      } catch (e) {}
    }

    this.init();
  }

  init() {
    this.buttons = this.el.querySelectorAll('.variant-option');
    this.buttons.forEach(btn => {
      btn.addEventListener('click', () => this.selectOption(btn));
    });

    // Set initial state
    const firstSelected = this.el.querySelector('.variant-option.is-selected');
    if (!firstSelected && this.buttons.length) {
      this.buttons[0].classList.add('is-selected');
    }
  }

  selectOption(btn) {
    const group = btn.closest('[data-option-group]');
    if (group) {
      group.querySelectorAll('.variant-option').forEach(b => b.classList.remove('is-selected'));
    }
    btn.classList.add('is-selected');
    this.updateVariant();
  }

  updateVariant() {
    if (!this.productData) return;
    const selectedValues = [];
    this.el.querySelectorAll('[data-option-group]').forEach((group, i) => {
      const selected = group.querySelector('.variant-option.is-selected');
      if (selected) {
        selectedValues.push(selected.dataset.value);
        const label = document.getElementById('option-value-' + i);
        if (label) label.textContent = selected.dataset.value;
      }
    });

    const variant = this.productData.variants.find(v =>
      v.options.every((opt, i) => opt === selectedValues[i])
    );

    if (!variant) return;

    // Update hidden input
    const idInput = this.form && this.form.querySelector('[name="id"]');
    if (idInput) idInput.value = variant.id;

    // Swap main image to this variant's photo (e.g. Black vs White socks)
    if (variant.featured_image && variant.featured_image.src) {
      const mainImg = document.getElementById('product-main-image');
      if (mainImg) {
        const base = variant.featured_image.src.split('?')[0];
        const q = w => base + '?width=' + w;
        mainImg.removeAttribute('srcset');
        mainImg.src = q(1200);
        mainImg.srcset = q(600) + ' 600w, ' + q(900) + ' 900w, ' + q(1200) + ' 1200w';
        mainImg.dataset.zoomSrc = q(1800);
        if (variant.featured_image.alt) mainImg.alt = variant.featured_image.alt;
        // Highlight the matching thumbnail if it exists
        const variantPath = variant.featured_image.src.split('?')[0];
        document.querySelectorAll('.product-gallery__thumb').forEach(thumb => {
          const thumbPath = (thumb.dataset.fullSrc || '').split('?')[0];
          thumb.classList.toggle('is-active', thumbPath === variantPath);
        });
      }
    }

    // Update add-to-cart button availability
    const addBtn = document.getElementById('add-to-cart-btn');
    if (addBtn) {
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }

    // Update price display
    const priceEl = document.querySelector('[data-product-price]');
    const comparePriceEl = document.querySelector('[data-compare-price]');
    if (priceEl) {
      priceEl.textContent = Utils.formatMoney(variant.price);
      priceEl.classList.toggle('is-sale', !!variant.compare_at_price && variant.compare_at_price > variant.price);
    }
    if (comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePriceEl.textContent = Utils.formatMoney(variant.compare_at_price);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }
}

// ============================================
// TESTIMONIALS SLIDER
// ============================================

class TestimonialsSlider {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.testimonials__track');
    this.slides = el.querySelectorAll('.testimonials__slide');
    this.dots = el.querySelectorAll('.testimonials__dot');
    this.prevBtn = el.querySelector('.testimonials__nav--prev');
    this.nextBtn = el.querySelector('.testimonials__nav--next');
    this.currentIndex = 0;
    this.autoplayInterval = null;
    this.autoplayDelay = 5000;
    this.touchStartX = 0;
    this.touchEndX = 0;

    if (!this.track || this.slides.length < 2) return;
    this.init();
  }

  init() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.prev(); this.resetAutoplay(); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.next(); this.resetAutoplay(); });

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { this.goTo(i); this.resetAutoplay(); });
    });

    // Touch/swipe
    this.track.addEventListener('touchstart', e => { this.touchStartX = e.touches[0].clientX; }, { passive: true });
    this.track.addEventListener('touchend', e => {
      this.touchEndX = e.changedTouches[0].clientX;
      const diff = this.touchStartX - this.touchEndX;
      if (Math.abs(diff) > 40) {
        diff > 0 ? this.next() : this.prev();
        this.resetAutoplay();
      }
    });

    this.startAutoplay();
    this.updateDots();
  }

  goTo(index) {
    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;
    this.updateDots();
  }

  next() { this.goTo(this.currentIndex + 1); }
  prev() { this.goTo(this.currentIndex - 1); }

  updateDots() {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentIndex);
    });
  }

  startAutoplay() {
    this.autoplayInterval = setInterval(() => this.next(), this.autoplayDelay);
  }

  resetAutoplay() {
    clearInterval(this.autoplayInterval);
    this.startAutoplay();
  }

  destroy() {
    clearInterval(this.autoplayInterval);
  }
}

// ============================================
// ACCORDION
// ============================================

class Accordion {
  constructor(container) {
    this.container = container;
    this.items = container.querySelectorAll('.accordion-item');
    this.init();
  }

  init() {
    this.items.forEach(item => {
      const trigger = item.querySelector('.accordion-trigger');
      if (!trigger) return;
      trigger.addEventListener('click', () => this.toggle(item));
    });

    // Open first by default
    if (this.items.length) {
      this.open(this.items[0]);
    }
  }

  toggle(item) {
    item.classList.contains('is-open') ? this.close(item) : this.open(item);
  }

  open(item) {
    item.classList.add('is-open');
  }

  close(item) {
    item.classList.remove('is-open');
  }
}

// ============================================
// COLLECTION FILTERS (mobile toggle)
// ============================================

class CollectionFilters {
  constructor() {
    this.sidebar = document.querySelector('.collection-filters');
    this.toggleBtn = document.querySelector('[data-filters-toggle]');
    if (!this.sidebar || !this.toggleBtn) return;
    this.init();
  }

  init() {
    this.toggleBtn.addEventListener('click', () => {
      this.sidebar.classList.toggle('is-open');
      const isOpen = this.sidebar.classList.contains('is-open');
      this.toggleBtn.textContent = isOpen ? 'Hide Filters' : 'Show Filters';
    });
  }
}

// ============================================
// SORT SELECT
// ============================================

class SortSelect {
  constructor() {
    const select = document.querySelector('.sort-select');
    if (!select) return;
    select.addEventListener('change', e => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      window.location.href = url.toString();
    });
  }
}

// ============================================
// INIT
// ============================================

document.addEventListener('DOMContentLoaded', () => {
  // Global instances
  new AnnouncementBar();
  new StickyHeader();
  new MobileNav();
  new SearchBar();
  new AddToCart();
  new CollectionFilters();
  new SortSelect();

  // Cart drawer (global)
  window.cartDrawer = new CartDrawer();

  // Product gallery
  const gallery = document.querySelector('.product-gallery');
  if (gallery) new ProductGallery(gallery);

  // Variant selectors
  document.querySelectorAll('.product-variants').forEach(el => new VariantSelector(el));

  // Testimonials
  document.querySelectorAll('.testimonials').forEach(el => new TestimonialsSlider(el));

  // Accordions
  document.querySelectorAll('.product-accordion').forEach(el => new Accordion(el));
});
